import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AppInformationService, AuthService, BrewersUnitOfWork, SessionInformationService, UtilityService } from 'app/core/core.module';
import { InvoiceService } from 'app/core/invoice.service';
import { Invoice, InvoiceStatus, InvoiceType } from 'app/core/model/entity-model';
import { ReportService } from 'app/core/report.service';
import { SharedModule } from 'app/shared/shared.module';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert, confirm } from 'devextreme/ui/dialog';
import _ from 'lodash';

@Component({
    standalone: false,
    selector: 'app-invoice-overview',
    templateUrl: './invoice-overview.component.html',
    styleUrls: ['./invoice-overview.component.scss'],
})
export class InvoiceOverviewComponent implements OnInit {
    uow = inject(BrewersUnitOfWork);
    router = inject(Router);
    auth = inject(AuthService);
    appInfo = inject(AppInformationService);
    reportService = inject(ReportService);
    utility = inject(UtilityService);
    invoiceService = inject(InvoiceService);
    sessionInfo = inject(SessionInformationService);


    invoices: Invoice[];
    statuses: InvoiceStatus[];
    filter: any = { showAll: false };

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;
    onExporting = SharedModule.onExporting;

    async ngOnInit() {
        const filter = this.sessionInfo.getItem('invoice-overview-filter');
        this.filter = filter ?? { showAll: false };
        await this.getData();
    }

    async getData() {
        const statuses = await this.uow.invoiceStatuses.whereActive();
        const types = await this.uow.invoiceTypes.whereActive();
        const customers = await this.uow.customers.getActiveCustomers();
        const invoices = (await this.uow.invoices.getInvoices('invoiceLines.load.ticketAttachment', {take: this.filter.showAll ? null : 100})) as Invoice[];
        invoices.forEach((invoice) => {
            const ticketTypes = invoice.invoiceLines.filter((line) => line.load?.ticketAttachmentTypeName).map((line) => line.load?.ticketAttachmentTypeName);
            (invoice as any).ticketType = _(ticketTypes).uniq().valueOf().join(', ');
        });
        this.invoices = invoices;
    }

    async toggleShowAll() {
        this.dataGrid.instance.clearSelection();
        this.filter.showAll = !this.filter.showAll;
        await this.getData();
        this.sessionInfo.setItem('invoice-overview-filter', this.filter);
    }

    selectionChange(e) {}

    signedAmount(data: Invoice, value: number) {
        return data.invoiceTypeId === InvoiceType.Types.Credit ? -value : value;
    }

    calculateSubTotal = (data: Invoice) => this.signedAmount(data, data.subTotal);
    calculateTax = (data: Invoice) => this.signedAmount(data, data.tax);
    calculateTotal = (data: Invoice) => this.signedAmount(data, data.total);
    calculateBalance = (data: Invoice) => this.signedAmount(data, data.balance);

    async open(invoice?: any) {
        this.router.navigate(['/invoices/' + (!!invoice?.id ? invoice.id : 'new')]);
    }

    async delete() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (!selectedRows) return;

        const result = await confirm(`Are you sure you want to delete the selected invoice${selectedRows.length > 1 ? 's' : ''}?`, this.appInfo.appTitle);
        if (!result) return;

        this.appInfo.pleaseWaitShow();

        const invoices = (await this.uow.invoices.byIds(
            selectedRows.map((x) => x.id),
            'invoiceLines'
        )) as any as Invoice[];
        console.log(invoices);

        invoices.forEach((invoice) => {
            invoice.deleted = new Date();
            invoice.deletedBy = this.auth.currentUser.userName;
            invoice.invoiceLines.forEach((line) => {
                line.loadId = null;
                line.deleted = new Date();
                line.deletedBy = this.auth.currentUser.userName;
            });
        });

        await this.uow.commit();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    async rowDoubleClick(e) {
        if (!e.data || !e.data.id) return;
        await this.open(e.data);
    }

    async print(invoices: Invoice[]) {
        if (!invoices || invoices.length === 0) return;

        this.appInfo.pleaseWaitShow();
        await this.reportService.printRequest(invoices);
        this.appInfo.pleaseWaitHide();
    }

    async sendToQueue(invoices: Invoice[]) {
        if (!invoices || invoices.length === 0) return;

        const result = await confirm(`Are you sure you want to email the selected invoice(s)?`, this.appInfo.settings.company);
        if (!result) return;

        this.appInfo.pleaseWaitShow();
        const ids = invoices.map((invoice) => invoice.id);
        const response = await this.utility.sendToQueue(ids);
        this.appInfo.pleaseWaitHide();
        await alert(response.success ? 'Invoices added to queue' : `Error adding invoices to queue. ${response.message}`, this.appInfo.settings.company);
    }

    async quickFinanceCharge(invoices: Invoice[]) {
        if (!invoices || invoices.length === 0) return;

        if (!invoices.every((invoice) => invoice.customerId === invoices[0].customerId)) {
            await alert('All invoices must have the same customer', 'Charge Not Created');
            return;
        }

        const invoiceIds = invoices.map((invoice) => invoice.id);
        const response = await this.invoiceService.createQuickFinanceCharge(invoiceIds);
        if (!response.success) {
            await alert(response.message, 'Charge Not Created');
            return;
        }

        await this.router.navigate([`/invoices/${response.value}`]);
    }
}
